import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * AffiliateTripStatusPage — the no-login /t/{token} page.
 *
 * Locked design: Docs/Build_Execution/Session_Reports/
 * 2026-09-08_bl187-gate1-live-and-handoff.md, "ADDENDUM - AFFILIATE FEATURE
 * LOCKED, FULL BUILD APPROVED".
 *
 * Who is looking at this: an affiliate driver who does not work for us, has no
 * account, did not choose to install anything, and is holding a phone one-handed
 * at a curb, possibly in daylight, possibly in a hurry. Every decision below
 * follows from that.
 *
 *   - One button, never four. The addendum is explicit, and it is also the only
 *     way to make an out-of-order tap impossible rather than merely invalid.
 *   - The button is enormous and says what the driver is about to do ("I've
 *     Arrived"), not our status code.
 *   - The ladder above it shows what has already been recorded, so a driver who
 *     is not sure whether their last tap went through can see the answer instead
 *     of tapping again.
 *   - Nothing here reads or writes Supabase directly. affiliate_trip_links has
 *     RLS on with zero policies and is unreachable from a browser by design; the
 *     token in the URL is the credential, and it only ever goes to our own
 *     server route.
 */

const STATUS_ENDPOINT = '/api/affiliate/trip-status';

// What went wrong, in words a driver can act on. The server's reasons are
// deliberately specific so this map can be too — "error" tells them nothing.
const ERROR_MESSAGES = {
  link_not_found: 'This link is not valid. Check the message you were sent, or call dispatch.',
  link_expired: 'This link has expired. Call dispatch for a new one.',
  token_required: 'This link is incomplete. Check the message you were sent.',
  already_recorded: 'That was already recorded — you are all set.',
  out_of_sequence: 'Something moved on. The button below is up to date.',
  trip_already_complete: 'This trip is already finished. Nothing more to do.',
  unknown_status: 'Something went wrong. Please try again.',
};

function messageFor(reason, fallback = 'Something went wrong. Please try again.') {
  return ERROR_MESSAGES[reason] || fallback;
}

function formatTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function StepRow({ step }) {
  const stateClass = step.done
    ? 'bg-brand-600 border-brand-600 text-white'
    : step.current
      ? 'bg-white border-brand-600 text-brand-700'
      : 'bg-slate-100 border-slate-200 text-slate-400';

  return (
    <li className="flex items-center gap-3">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${stateClass}`}
        aria-hidden="true"
      >
        {step.done ? '✓' : ''}
      </span>
      <span className="flex-1">
        <span
          className={`block text-base font-semibold ${
            step.done ? 'text-slate-900' : step.current ? 'text-slate-900' : 'text-slate-400'
          }`}
        >
          {step.label}
        </span>
        {step.recordedAt && (
          <span className="block text-sm text-slate-500">Recorded {formatTime(step.recordedAt)}</span>
        )}
      </span>
    </li>
  );
}

function AffiliateTripStatusPage() {
    const { token } = useParams();

    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    // A blocking problem — no trip to show at all.
    const [fatalError, setFatalError] = useState('');
    // A non-blocking note shown above a still-usable page.
    const [notice, setNotice] = useState('');

    const loadTrip = useCallback(async () => {
        if (!token) {
            setFatalError(messageFor('token_required'));
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${STATUS_ENDPOINT}?token=${encodeURIComponent(token)}`);
            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                setFatalError(messageFor(body.error, 'This link could not be opened. Call dispatch.'));
                setTrip(null);
            } else {
                setTrip(body.trip || null);
                setFatalError('');
            }
        } catch {
            setFatalError('Could not reach the network. Check your signal and try again.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadTrip();
    }, [loadTrip]);

    // Breaker Panel D6: a TEST trip page is never indexed by search engines.
    const isTestTrip = Boolean(trip?.test);
    useEffect(() => {
        if (!isTestTrip || typeof document === 'undefined') return undefined;
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => meta.remove();
    }, [isTestTrip]);

    const handleTap = async () => {
        if (!trip?.nextStatus || submitting) return;

        setSubmitting(true);
        setNotice('');

        try {
            const response = await fetch(STATUS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, status: trip.nextStatus }),
            });
            const body = await response.json().catch(() => ({}));

            // A 409 is not a dead end: the server sends the current view back with
            // it, so a double-tap on a bad signal, or a page left open while the
            // trip moved on, just re-renders the truth.
            // The TEST flag (Breaker Panel) comes with the page read; keep it across taps.
            if (body.trip) setTrip((previous) => ({ ...body.trip, test: body.trip.test ?? previous?.test }));

            if (!response.ok) {
                setNotice(messageFor(body.error));
            }
        } catch {
            // The tap may or may not have landed. Rather than guess, re-read the
            // real state — tapping again on an unknown outcome is how you get a
            // driver mashing a button.
            setNotice('Could not confirm that. Checking the latest status…');
            await loadTrip();
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <p className="text-lg text-slate-500">Loading your trip…</p>
            </main>
        );
    }

    if (fatalError) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-sm">
                    <h1 className="mb-2 text-xl font-bold text-slate-900">Trip status</h1>
                    <p className="text-base text-slate-600">{fatalError}</p>
                </div>
            </main>
        );
    }

    if (!trip) return null;

    return (
        <main className="min-h-screen bg-slate-50 px-4 py-6">
            <div className="mx-auto w-full max-w-md">
                {trip.test && (
                    // Breaker Panel D6: this company's Dispatch is in Test — say so plainly on the public page.
                    <p role="status" data-testid="trip-status-test-banner"
                       className="mb-4 rounded-lg border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-900">
                        TEST — this trip link is for testing. No real trip is behind it.
                    </p>
                )}
                <header className="mb-5">
                    <h1 className="text-2xl font-bold text-slate-900">Trip status</h1>
                    {trip.confirmationNumber && (
                        <p className="text-base text-slate-500">
                            Confirmation {trip.confirmationNumber}
                        </p>
                    )}
                </header>

                {(trip.pickupAddress || trip.dropoffAddress) && (
                    <section className="mb-5 rounded-2xl bg-white p-4 shadow-sm">
                        {trip.pickupAddress && (
                            <div className="mb-3">
                                <p className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                                    Pickup
                                </p>
                                <p className="text-base text-slate-900">{trip.pickupAddress}</p>
                            </div>
                        )}
                        {trip.dropoffAddress && (
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                                    Drop-off
                                </p>
                                <p className="text-base text-slate-900">{trip.dropoffAddress}</p>
                            </div>
                        )}
                    </section>
                )}

                <section className="mb-5 rounded-2xl bg-white p-4 shadow-sm">
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
                        Progress
                    </h2>
                    <ol className="flex flex-col gap-3">
                        {trip.steps.map((step) => (
                            <StepRow key={step.status} step={step} />
                        ))}
                    </ol>
                </section>

                {/* aria-live so a screen reader announces the outcome of a tap;
                    the visual change alone would be silent. */}
                <p aria-live="polite" className="min-h-6">
                    {notice && (
                        <span className="mb-3 block rounded-xl bg-amber-50 px-4 py-3 text-base text-amber-900">
                            {notice}
                        </span>
                    )}
                </p>

                {trip.complete ? (
                    <div className="rounded-2xl bg-brand-50 p-6 text-center">
                        <p className="text-xl font-bold text-brand-800">Trip complete</p>
                        <p className="mt-1 text-base text-brand-700">
                            Thanks — nothing else is needed.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* One button, never four. Out-of-order taps are made
                            impossible here rather than merely rejected later. */}
                        <button
                            type="button"
                            onClick={handleTap}
                            disabled={submitting}
                            // min-h-[4rem] keeps the target well above the ~44px
                            // floor for a thumb on a phone held one-handed.
                            className="w-full rounded-2xl bg-brand-600 px-6 py-5 text-xl font-bold text-white shadow-sm transition active:bg-brand-700 disabled:opacity-60 min-h-[4rem]"
                        >
                            {submitting ? 'Sending…' : trip.nextStatusAction}
                        </button>
                        <p className="mt-3 text-center text-base text-slate-500">
                            Next: {trip.nextStatusLabel}
                        </p>
                    </>
                )}
            </div>
        </main>
    );
}

export default AffiliateTripStatusPage;
